import React, { useEffect, useRef, useState } from "react";
import styles from "./AdminUserEdit.module.css";
import Select from "react-select";
import { Link, useHistory, useParams } from "react-router-dom";
import CustomerSearchPopup from "../Customers/CustomerSearchPopup";
import { get, post, put } from "../../Common/api";
import { useDispatch } from "react-redux";
import Alert, {
  success as successNotificationType,
  error as errorNotificationType,
  getNotificationForError,
} from "../Alert";
import {
  setCurrentPageNotification,
  setNextPageNotification,
} from "../../redux/notificationSlice";
import { Error } from "@progress/kendo-react-labels";
import { Input } from "@progress/kendo-react-inputs";

export default function AdminUserEdit() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isMultiSelectionEnabled] = useState(false);
  const [readOnlyEmail, setReadonlyEmail] = useState(false);
  const [customer, setCustomer] = useState({ id: -1, name: "", email: "" });
  const [adminRoles, setAdminRoles] = useState([]);
  const [visible, setVisible] = useState(false);
  const [hasFormErrors, setHasFormErrors] = useState(false);
  const [customerValidationMessage, setCustomerValidationMessage] =
    useState("");
  const [emailValidationMessage, setEmailValidationMessage] = useState("");
  const [roleValidationMessage, setRoleValidationMessage] = useState("");
  const [errorFlashClassName, setErrorFlashClassName] = useState("");
  const [errorBorderFlashClassName, setErrorBorderFlashClassName] =
    useState("");
  const [email, setEmail] = useState("");
  const [selectedAdminRole, setSelectedAdminRole] = useState(null);

  const firstTimeValidation = useRef(true);

  const toggleDialog = () => {
    if(id) return;
    setVisible(!visible);
  };

  const redirectToAdminUserList = () => {
    history.push("/adminusers");
  };

  const onRoleSelectionChange = (newSelectedRole) => {
    setSelectedAdminRole(newSelectedRole);
  };

  const customerSelectionCallback = (selectedCustomers) => {
    if (selectedCustomers && selectedCustomers.length > 0) {
      setCustomer({
        ...customer,
        id: selectedCustomers[0].id,
        name: `${selectedCustomers[0].lastName}, ${
          selectedCustomers[0].firstName
        } ${selectedCustomers[0].middle || ""}`,
      });
    }
  };

  const isValidEmail = (email)=>{
    let result = !!email && email.trim() !== '';
    return result;
  };

  useEffect(async () => {

    window.scrollTo(0, 0);

    async function fetchData() {
      try {
        const result = await get("/adminroles");
        const options = result.map((i) => {
          return {
            value: i.id,
            label: i.name,
          };
        });
        return options;
      } catch (error) {
        dispatch(setCurrentPageNotification(getNotificationForError(error)));
      }
    }

    if (id) {
      setReadonlyEmail(true);
      Promise.all([
        get(`/adminusers/${id}`).catch((error) => {
          dispatch(setNextPageNotification(getNotificationForError(error)));
          redirectToAdminUserList();
        }),
       fetchData()
      ]).then(values => {
        const options = values[1];
        setAdminRoles(options);
        const savedUser = values[0];
        if(savedUser) {
          setCustomer({
            ...customer,
            id: savedUser.customerId,
            name: savedUser.name
          });
          setEmail(savedUser.identityServiceUsername);
          setReadonlyEmail(isValidEmail(savedUser.identityServiceUsername));
          setSelectedAdminRole(options.find((option)=> option.value === savedUser.adminRoleId));
        }
      });
    }
    else{
    var options = await fetchData();
    setAdminRoles(options);
  }
  }, [id, dispatch]);

  const updateUser = async() => {
    // User without email
    let user = {
      Id: id,
      userRoleId: selectedAdminRole.value,
      customerId: customer.id
    };

    // Only submit email if it is being updated
    if (!readOnlyEmail && email) {
      user = { email, ...user };
    }

    try {
      await put(`/adminusers`, user);
      dispatch(
        setNextPageNotification({
          type: successNotificationType,
          message: `Admin user updated.`,
        })
      );
      history.push("/adminusers");
    } catch (errorObj) {
      if (errorObj && errorObj.data && errorObj.data.type === "validation") {
        const errors = errorObj.data.errors;
        if (errors) {
          resetErrors();
          
          if (errors["role"]) {
            setRoleValidationMessage(errors["role"]);
          }
          if (errors["email"]) {
            setEmailValidationMessage(errors["email"]);
          }          
          flashErrors();
          setHasFormErrors(true);
        }
      } else {
        dispatch(setCurrentPageNotification(getNotificationForError(errorObj)));
      }
    }

  };

  const saveUser = async (e) => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    if(id)
    {
      updateUser();
    }
    else{
      const user = {
        customerId: customer.id,
        username: email,
        identityServiceUsername: email,
        userRoleId: selectedAdminRole.value,
      };
      try {
        await post("/adminusers", user);
        dispatch(
          setNextPageNotification({
            type: successNotificationType,
            message: `Admin user saved.`,
          })
        );
        history.push("/adminusers");
      } catch (errorObj) {
        if (errorObj && errorObj.data && errorObj.data.type === "validation") {
          const errors = errorObj.data.errors;
          if (errors) {
            resetErrors();
            if (errors["customer"]) {
              setCustomerValidationMessage(errors["customer"]);
            }
            if (errors["email"]) {
              setEmailValidationMessage(errors["email"]);
            }
            if (errors["role"]) {
              setRoleValidationMessage(errors["role"]);
            }
            flashErrors();
            setHasFormErrors(true);
          }
        } else {
          dispatch(setCurrentPageNotification(getNotificationForError(errorObj)));
        }
      }
   }
  };

  const validateForm = () => {
    let isValid = true;
    resetErrors();
    if (customer.name == null || customer.name === "") {
      setCustomerValidationMessage("Customer is required");
      isValid = false;
    }
    if(id === undefined)
    {
      if (email == null || email === "") {
        setEmailValidationMessage("Login Email is required");
        isValid = false;
      } else if (!(email.includes("@") && email.includes("."))) {
        setEmailValidationMessage(`Email '${email}' is invalid.`);
        isValid = false;
      }
    }
    if (!selectedAdminRole) {
      setRoleValidationMessage("Admin Role is required");
      isValid = false;
    }
    setHasFormErrors(!isValid);
    flashErrors();
    return isValid;
  };

  const resetErrors = () => {
    setCustomerValidationMessage("");
    setEmailValidationMessage("");
    setRoleValidationMessage("");
  };

  const flashErrors = () => {
    if (hasFormErrors && !firstTimeValidation.current) {
      setErrorFlashClassName(`${styles.flash_error_msg}`);
      setErrorBorderFlashClassName(`${styles.flash_error_border}`);
      setTimeout(() => {
        setErrorFlashClassName("");
        setErrorBorderFlashClassName("");
      }, 1000);
    }
    firstTimeValidation.current = false;
  };

  return (
    <React.Fragment>
      <Alert />
      {visible && (
        <CustomerSearchPopup
          selectedCustomerIds={[customer.id]}
          toggleDialog={toggleDialog}
          customerSelectionCallback={customerSelectionCallback}
          isMultiSelectionEnabled={isMultiSelectionEnabled}
        />
      )}
      <div className="container">
        <div className="row">
          {hasFormErrors && (
            <div className={"k-messagebox k-messagebox-error"}>
              {"One or more fields are invalid"}
            </div>
          )}
        </div>
        <div className="row">
          <div className="col-md-6">
            <div>
              <label htmlFor="txtCustomer">
                Customer<span style={{ color: "red" }}>*</span>
              </label>
            </div>
            <div>
              <Input
                type="text"
                id="txtCustomer"
                className={`${styles.textBoxName} ${
                  customerValidationMessage ? errorBorderFlashClassName : ""
                } ${customerValidationMessage ? styles.error : ""}`}
                readOnly={true}
                placeholder="Customer"
                value={customer.name}
                onClick={toggleDialog}
              ></Input>
              {customerValidationMessage && (
                <Error className={`${errorFlashClassName}`}>
                  {customerValidationMessage}
                </Error>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6" style={{ marginTop: "30px" }}>
            <div>
              <label htmlFor="txtEmail">
                Login Email<span style={{ color: "red" }}>*</span>
              </label>
            </div>
            <div>
              <Input
                type="text"
                id="txtEmail"
                name={"email"}
                className={`${styles.textBoxName} ${
                  emailValidationMessage ? errorBorderFlashClassName : ""
                } ${emailValidationMessage ? styles.error : ""}`}
                placeholder="Email"
                value={email}
                readOnly={readOnlyEmail}
                onChange={(event) => setEmail(event.target.value)}
              ></Input>
              {emailValidationMessage && (
                <Error className={`${errorFlashClassName}`}>
                  {emailValidationMessage}
                </Error>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6" style={{ marginTop: "30px" }}>
            <div>
              <label htmlFor="ddlRole">Admin Role</label>
              <span style={{ color: "red" }}>*</span>
            </div>
            <div>
              <Select
                value={selectedAdminRole}
                options={adminRoles}
                closeMenuOnSelect={true}
                onChange={onRoleSelectionChange}
                name={"role"}
                className={`${
                  roleValidationMessage ? errorBorderFlashClassName : ""
                } ${roleValidationMessage ? styles.error : ""}`}
                inputId="ddlRole"
              ></Select>
              {roleValidationMessage && (
                <Error className={`${errorFlashClassName}`}>
                  {roleValidationMessage}
                </Error>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div>
              <Link to="/adminusers">
                <input
                  type="button"
                  className="btn default btn-lg pull-right"
                  style={{ marginTop: "20px" }}
                  value="Cancel"
                ></input>
              </Link>
              <input
                type="button"
                className="btn blue btn-lg pull-right"
                style={{ margin: "20px 10px 0px 0px" }}
                value="Save"
                onClick={saveUser}
              ></input>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
